import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
    faChevronDown,
    faChevronRight,
    faInfoCircle,
} from "@fortawesome/pro-regular-svg-icons";
import {
    faExclamationTriangle,
    faTimes,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Modal from "@ts-digital/vrc/es/components/modal";
import classnames from "classnames";
import { find, includes, isEmpty, isNil, reduce, some } from "lodash";
import numbro from "numbro";
import { useState } from "react";
import { FormattedDate, FormattedNumber } from "react-intl";
import {
    PaymentRequestStatus,
    PaymentStatus,
} from "../../../PaymentContainerClient/commons/enums";
import PaymentRequestDto from "../../../PaymentContainerClient/dtos/PaymentRequestDto";
import getFormattedMessageWithScope from "../../../utils/getFormattedMessageWithScope";
import {
    getTotalRemaining,
    hasExpired,
    isInAtLeastOnePaymentMethodConstraintsRange,
} from "../../commons/calculators";
import getFirstPaymentWithAtLeastOneSelectedInstallments from "../../commons/getFirstPaymentWithAtLeastOneSelectedInstallments";
import getFirstScheduledInstallment from "../../commons/getFirstScheduledInstallment";
import getInstallmentById from "../../commons/getInstallmentById";
import getLastNotPaidNotScheduledInstallment from "../../commons/getLastNotPaidNotScheduledInstallment";
import { CustomButton } from "../CustomButton";
import { useWindowDimensions } from "../WindowDimensionsProvider";
import { AttachedDocuments } from "./AttachedDocuments";
import "./style.css";
import { SummaryBox } from "./SummaryBox";
import womanSit from "./womanSit.svg";

const fm = getFormattedMessageWithScope("components.Summary");

interface SummaryProps {
    paymentRequest?: PaymentRequestDto;
    error?: any;
    onClickNext: Function;
    currentStep: number;
    selectedInstallmentIds: string[];
    selectedPaymentMethodName?: string;
    onSubmit: Function;
    isCreatingPayment: boolean;
    onOpenAlreadyPaidModal: Function;
    onOpenPaymentAlreadyExistModal: Function;
    isPayNow: boolean;
    selectedPaymentPlanId?: string;
    onDownloadDocument: Function;
    isDownloadingDocument: boolean;
    documentIdDownloading?: string;
}

export const Summary = ({
    paymentRequest,
    error,
    onClickNext,
    currentStep,
    selectedInstallmentIds,
    selectedPaymentMethodName,
    onSubmit,
    isCreatingPayment,
    onOpenAlreadyPaidModal,
    onOpenPaymentAlreadyExistModal,
    isPayNow,
    selectedPaymentPlanId,
    onDownloadDocument,
    isDownloadingDocument,
    documentIdDownloading,
}: SummaryProps) => {
    const { width } = useWindowDimensions();
    const [isOnHoldPaymentsWarningVisible, setIsOnHoldPaymentsWarningVisible] =
        useState(true);
    const [isSummaryBoxModalOpen, setIsSummaryBoxModalOpen] = useState(false);
    const [
        isTotalPayRemainingAccordionOpen,
        setIsTotalPayRemainingAccordionOpen,
    ] = useState(false);

    function toggleOnHoldPaymentsWarning() {
        setIsOnHoldPaymentsWarningVisible(!isOnHoldPaymentsWarningVisible);
    }

    function toggleTotalPayRemainingAccordion() {
        setIsTotalPayRemainingAccordionOpen(!isTotalPayRemainingAccordionOpen);
    }

    function toggleSummaryBoxModal(e: any) {
        // Warning: we need this check because 1/100 time Chrome browser on iOS
        // throws an - e is undefined - error
        if (e) {
            e.stopPropagation();
        }
        setIsSummaryBoxModalOpen(!isSummaryBoxModalOpen);
    }

    const selectedInstallments =
        paymentRequest && !isEmpty(selectedInstallmentIds)
            ? selectedInstallmentIds.map((selectedInstallmentId) =>
                  getInstallmentById(selectedInstallmentId, paymentRequest)
              )
            : undefined;

    const isAtLeastOnePaymentOnHold =
        paymentRequest &&
        some(
            paymentRequest.payments,
            (payment) => payment.status === PaymentStatus.OnHold
        );

    function onNext() {
        if (currentStep !== 2) {
            onClickNext();
        } else if (
            !isNil(
                getFirstPaymentWithAtLeastOneSelectedInstallments(
                    paymentRequest!.payments,
                    selectedInstallmentIds
                )
            )
        ) {
            onOpenPaymentAlreadyExistModal();
        } else {
            onSubmit();
        }
    }

    function renderAlreadyPaidButton() {
        return (
            currentStep === 0 &&
            !isAtLeastOnePaymentOnHold && (
                <CustomButton
                    kind="tertiary"
                    onClick={onOpenAlreadyPaidModal}
                    className="c-Summary-alreadyPaid-button"
                >
                    {fm("alreadyPaid")}
                </CustomButton>
            )
        );
    }

    function renderNextButton() {
        return (
            <CustomButton
                kind="primary"
                className={classnames(
                    "c-Summary-next-button",
                    currentStep === 2
                        ? "c-Summary-next-button-last-step"
                        : undefined
                )}
                onClick={onNext}
                loading={isCreatingPayment}
                disabled={
                    (currentStep === 1 && isEmpty(selectedInstallments)) ||
                    (currentStep === 2 &&
                        (isEmpty(selectedPaymentMethodName) ||
                            isEmpty(selectedInstallments) ||
                            (selectedInstallments &&
                                !isInAtLeastOnePaymentMethodConstraintsRange(
                                    reduce(
                                        selectedInstallments,
                                        (result, selectedInstallment) =>
                                            numbro(
                                                selectedInstallment.amount
                                                    .amount
                                            )
                                                .add(result)
                                                .value(),
                                        0
                                    ).toString(),
                                    paymentRequest!
                                ))))
                }
            >
                {fm(currentStep !== 2 ? "next" : "proceedToPayment")}
            </CustomButton>
        );
    }

    function renderButtonsBar() {
        if (
            !error &&
            paymentRequest &&
            paymentRequest.status !== PaymentRequestStatus.Paid &&
            paymentRequest.status !== PaymentRequestStatus.ExternalPaid &&
            paymentRequest.status !== PaymentRequestStatus.Scheduled &&
            !hasExpired(paymentRequest)
        ) {
            return width > 1000 ? (
                <div className="c-Summary-buttons-bar">
                    <div className="c-Summary-buttons-bar-container">
                        <div className="c-Summary-buttons-bar-container-content">
                            {renderAlreadyPaidButton()}
                            {renderNextButton()}
                        </div>
                    </div>
                </div>
            ) : (
                <div
                    className={classnames(
                        "c-Summary-buttons-bar",
                        currentStep !== 0
                            ? "c-Summary-buttons-bar-only-one-button"
                            : undefined
                    )}
                >
                    <div className="c-Summary-buttons-bar-container">
                        {renderNextButton()}
                        {renderAlreadyPaidButton()}
                    </div>
                </div>
            );
        }

        return undefined;
    }

    const isPaidOrScheduled = includes(
        [PaymentRequestStatus.Paid, PaymentRequestStatus.Scheduled],
        paymentRequest?.status
    );

    const selectedPaymentPlan = find(paymentRequest?.availablePaymentPlans, {
        id: selectedPaymentPlanId,
    });

    const hasOnHoldPayments = some(paymentRequest?.payments, {
        status: PaymentStatus.OnHold,
    });

    return (
        <div className="c-Summary-container">
            {(hasOnHoldPayments ||
                paymentRequest?.status === PaymentRequestStatus.ExternalPaid) &&
                !isPaidOrScheduled &&
                isOnHoldPaymentsWarningVisible && (
                    <div className="c-Summary-onhold-payment-request-notification">
                        <div>
                            <FontAwesomeIcon
                                icon={faExclamationTriangle as IconProp}
                                className="c-Summary-onhold-payment-request-notification-icon"
                            />
                        </div>
                        <div className="c-Summary-onhold-payment-request-notification-message">
                            {fm(
                                hasOnHoldPayments
                                    ? "paymentOnHold"
                                    : "externalPaidDocumentMessage"
                            )}
                        </div>
                        <div>
                            <FontAwesomeIcon
                                icon={faTimes as IconProp}
                                onClick={toggleOnHoldPaymentsWarning}
                                className="c-Summary-onhold-payment-request-notification-close-icon"
                            />
                        </div>
                    </div>
                )}
            {isPaidOrScheduled &&
                paymentRequest &&
                !hasExpired(paymentRequest) && (
                    <div className="c-Summary-paid-payment-request-notification">
                        <div>
                            <FontAwesomeIcon
                                icon={faInfoCircle as IconProp}
                                className="c-Summary-paid-payment-request-notification-icon"
                            />
                        </div>
                        <div className="c-Summary-paid-payment-request-notification-message">
                            {fm(
                                paymentRequest.status ===
                                    PaymentRequestStatus.Paid
                                    ? "paidDocumentMessage"
                                    : {
                                          id: "scheduledPayDocumentMessage",
                                          values: {
                                              deadline: (
                                                  <FormattedDate
                                                      value={
                                                          getFirstScheduledInstallment(
                                                              selectedPaymentPlan?.installments ||
                                                                  [],
                                                              paymentRequest.payments
                                                          )?.dueDate ||
                                                          getLastNotPaidNotScheduledInstallment(
                                                              selectedPaymentPlan?.installments ||
                                                                  [],
                                                              paymentRequest.payments
                                                          )?.dueDate
                                                      }
                                                  />
                                              ),
                                          },
                                      }
                            )}
                        </div>
                    </div>
                )}
            <span className="c-Summary-title">{fm("summary")}</span>
            {width > 1000 ? (
                <>
                    <SummaryBox
                        paymentRequest={paymentRequest}
                        isPayNow={isPayNow}
                        selectedPaymentPlanId={selectedPaymentPlanId}
                    />
                    {paymentRequest && !hasExpired(paymentRequest) ? (
                        <AttachedDocuments
                            documents={paymentRequest.paymentDocuments}
                            onDownloadDocument={onDownloadDocument}
                            isDownloading={isDownloadingDocument}
                            documentIdDownloading={documentIdDownloading}
                            isPaid={
                                paymentRequest.status ===
                                PaymentRequestStatus.Paid
                            }
                            currentStep={currentStep}
                            hasNotificationForPaidRequest={
                                isPaidOrScheduled && !hasExpired(paymentRequest)
                            }
                            hasNotificationForOnHoldPayments={
                                hasOnHoldPayments &&
                                !isPaidOrScheduled &&
                                isOnHoldPaymentsWarningVisible
                            }
                        />
                    ) : undefined}
                </>
            ) : (
                <Modal
                    className="c-Summary-Modal"
                    visible={isSummaryBoxModalOpen}
                    onClose={toggleSummaryBoxModal}
                    title={
                        <span className="c-Summary-Modal-title">
                            {fm("summary")}
                        </span>
                    }
                >
                    <div className="c-Summary-Modal-content">
                        <SummaryBox
                            paymentRequest={paymentRequest}
                            isPayNow={isPayNow}
                            selectedPaymentPlanId={selectedPaymentPlanId}
                        />
                    </div>
                </Modal>
            )}
            {paymentRequest &&
            !hasExpired(paymentRequest) &&
            ((currentStep > 0 && selectedInstallments) || width <= 1000) ? (
                <div
                    className={classnames(
                        "c-Summary-totalToPayRemaining-container",
                        isPaidOrScheduled
                            ? "c-Summary-totalToPayRemaining-container-paid-or-scheduled-request"
                            : undefined,
                        width <= 1000 && currentStep !== 0
                            ? "c-Summary-totalToPayRemaining-container-only-one-button"
                            : undefined,
                        isTotalPayRemainingAccordionOpen
                            ? "c-Summary-totalToPayRemaining-container-open"
                            : undefined,
                        isOnHoldPaymentsWarningVisible
                            ? "c-Summary-totalToPayRemaining-container-onHoldNotificationOpen"
                            : undefined
                    )}
                >
                    <div
                        className="c-Summary-totalToPayRemaining-content"
                        onClick={toggleTotalPayRemainingAccordion}
                    >
                        <FontAwesomeIcon
                            icon={
                                (isTotalPayRemainingAccordionOpen
                                    ? faChevronDown
                                    : faChevronRight) as IconProp
                            }
                            className="c-Summary-totalToPayRemaining-arrow"
                        />
                        {fm(currentStep > 0 ? "totalToPay" : "totalRemaining")}
                        <div className="c-Summary-totalToPayRemaining-value">
                            <FormattedNumber
                                style="currency"
                                currency={paymentRequest.totalAmount.currency}
                                value={
                                    currentStep > 0
                                        ? numbro(
                                              reduce(
                                                  selectedInstallments,
                                                  (
                                                      result,
                                                      selectedInstallment
                                                  ) =>
                                                      numbro(
                                                          selectedInstallment!
                                                              .amount.amount
                                                      )
                                                          .add(result)
                                                          .value(),
                                                  0
                                              )
                                          ).value()
                                        : getTotalRemaining(
                                              paymentRequest.totalAmount.amount,
                                              paymentRequest.payments
                                          )
                                }
                            />
                        </div>
                        {isTotalPayRemainingAccordionOpen && (
                            <div>
                                <CustomButton
                                    kind="secondary"
                                    colorScheme="ghost"
                                    size="small"
                                    className="c-Summary-totalToPayRemaining-detailes-button"
                                    onClick={toggleSummaryBoxModal}
                                >
                                    {fm("details")}
                                </CustomButton>
                            </div>
                        )}
                    </div>
                </div>
            ) : undefined}
            {error || (paymentRequest && hasExpired(paymentRequest)) ? (
                <img
                    alt="woman"
                    className="c-Summary-woman-image"
                    src={womanSit}
                />
            ) : undefined}
            {renderButtonsBar()}
        </div>
    );
};
